import React, { useState } from "react";
import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";
import useToastAlert from "@/hooks/useToastAlert";
import {
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import FormGroup from "@/components/FormGroup";

interface Props {
  onClose(): void;
  onAdded(): void;
}

type WidgetType = "click_to_call" | "click_to_text" | "topic_chat" | "product_inquiry" | "knowledge_base";

const WIDGET_TYPES: { value: WidgetType; label: string; icon: string; description: string }[] = [
  {
    value: "knowledge_base",
    label: "Knowledge Base Q&A",
    icon: "fa-book-open",
    description: "A lightweight Q&A widget powered by your knowledge base. Visitors ask questions and get instant AI answers — no live agent needed. Perfect for FAQ pages, help centers, and product docs."
  },
  {
    value: "click_to_call",
    label: "Click to Call",
    icon: "fa-phone",
    description: "A floating phone button that slides out your phone number. Visitors tap to call."
  },
  {
    value: "click_to_text",
    label: "Click to Text",
    icon: "fa-comment-sms",
    description: "A floating SMS button. Visitors enter their number and you text them back."
  },
  {
    value: "topic_chat",
    label: "Topic Chat",
    icon: "fa-comments",
    description: 'An "Ask anything" chat prompt for a specific page — product questions, pricing, support.'
  },
  {
    value: "product_inquiry",
    label: "Product Inquiry",
    icon: "fa-tag",
    description: 'A smart widget that reads the product name from the page and opens a pre-contextualized conversation. Perfect for product pages, listings, and catalogs.'
  }
];

export default function AddWidgetModal({ onClose, onAdded }: Props) {
  const api = useApi();
  const { teams } = useTeams();
  const toastAlert = useToastAlert();

  const [step, setStep] = useState<"type" | "settings">("type");
  const [widgetType, setWidgetType] = useState<WidgetType | null>(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  // Settings per type
  const [phoneNumber, setPhoneNumber] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [panelHeading, setPanelHeading] = useState("");
  const [panelSubtitle, setPanelSubtitle] = useState("");
  const [greeting, setGreeting] = useState("Have questions? Just ask!");
  const [placeholder, setPlaceholder] = useState("Type your question here...");
  const [teamId, setTeamId] = useState<number>(teams[0]?.id ?? 0);
  const [primaryColor, setPrimaryColor] = useState("#0066CC");
  const [position, setPosition] = useState<"bottom-right" | "bottom-left">("bottom-right");
  // product_inquiry
  const [productNameSelector, setProductNameSelector] = useState("h1");
  const [productGreeting, setProductGreeting] = useState("Have a question about {productName}?");
  // knowledge_base
  const [kbGreeting, setKbGreeting] = useState("Ask us anything!");
  const [kbPlaceholder, setKbPlaceholder] = useState("Type your question...");
  const [showSources, setShowSources] = useState(true);
  const [escalateToChat, setEscalateToChat] = useState(true);

  function buildSettingsJson(): string {
    if (widgetType === "click_to_call") {
      return JSON.stringify({ phoneNumber, buttonText: buttonText || "📞 Call Us", panelHeading: panelHeading || "Give us a call", panelSubtitle, primaryColor, position });
    }
    if (widgetType === "click_to_text") {
      return JSON.stringify({ phoneNumber, buttonText: buttonText || "💬 Text Us", panelHeading: panelHeading || "Send us a text", panelSubtitle, primaryColor, position });
    }
    if (widgetType === "product_inquiry") {
      return JSON.stringify({ productNameSelector, greeting: productGreeting, placeholder: placeholder || "Ask your question...", buttonText: buttonText || "Ask a question", primaryColor, position, teamId });
    }
    if (widgetType === "knowledge_base") {
      return JSON.stringify({ greeting: kbGreeting, placeholder: kbPlaceholder, buttonText: buttonText || "❓ Ask a Question", showSources, escalateToChat, primaryColor, position, teamId });
    }
    // topic_chat
    return JSON.stringify({ greeting, placeholder, buttonText: buttonText || "💬 Chat", primaryColor, position, teamId });
  }

  async function handleSave() {
    if (!widgetType || !name.trim()) return;

    setSaving(true);
    try {
      const res = await api.admin.post("PageWidgets", {
        name: name.trim(),
        widgetType,
        settingsJson: buildSettingsJson(),
        displayRulesJson: "[]",
        isEnabled: true
      });

      if (res.ok) {
        toastAlert.displayToast(Severity.Success, "Widget created!");
        onAdded();
      } else {
        const err = await res.json().catch(() => ({}));
        toastAlert.displayToast(Severity.Error, err?.error ?? "Failed to create widget.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to create widget.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-stone-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="font-semibold text-lg">
            {step === "type" ? "Choose Widget Type" : "Configure Widget"}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        {/* Step 1 — Widget type picker */}
        {step === "type" && (
          <div className="p-6 flex flex-col gap-3">
            {WIDGET_TYPES.map((t) => (
              <button
                key={t.value}
                onClick={() => setWidgetType(t.value)}
                className={`flex items-start gap-4 p-4 rounded-lg border-2 text-left transition-all ${
                  widgetType === t.value
                    ? "border-primary bg-primary-bg"
                    : "border-gray-200 hover:border-cornflower-blue-300 hover:bg-gray-50"
                }`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  widgetType === t.value ? "bg-primary" : "bg-gray-100"
                }`}>
                  <i className={`fa-solid ${t.icon} ${widgetType === t.value ? "text-white" : "text-gray-500"}`} />
                </div>
                <div>
                  <div className="font-medium">{t.label}</div>
                  <div className="text-sm text-gray-500 mt-0.5">{t.description}</div>
                </div>
              </button>
            ))}

            <div className="flex justify-end gap-2 mt-2">
              <SecondaryBlueButton label="Cancel" onClick={onClose} />
              <PrimaryBlueButton
                label="Next"
                onClick={() => widgetType && setStep("settings")}
                disabled={!widgetType}
              />
            </div>
          </div>
        )}

        {/* Step 2 — Settings */}
        {step === "settings" && widgetType && (
          <div className="p-6 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
            <FormGroup label="Widget Name" hint="Used only in the admin panel">
              <input
                className="input w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={widgetType === "topic_chat" ? "Product FAQ Widget" : "Main Phone Widget"}
                autoFocus
              />
            </FormGroup>

            {(widgetType === "click_to_call" || widgetType === "click_to_text") && (
              <FormGroup label="Phone Number">
                <input
                  className="input w-full"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1-800-555-1234"
                />
              </FormGroup>
            )}

            {widgetType === "click_to_call" && (
              <FormGroup label="Panel Heading" hint="Optional">
                <input
                  className="input w-full"
                  value={panelHeading}
                  onChange={(e) => setPanelHeading(e.target.value)}
                  placeholder="Give us a call"
                />
              </FormGroup>
            )}

            {widgetType === "click_to_text" && (
              <FormGroup label="Panel Heading" hint="Optional">
                <input
                  className="input w-full"
                  value={panelHeading}
                  onChange={(e) => setPanelHeading(e.target.value)}
                  placeholder="Send us a text"
                />
              </FormGroup>
            )}

            {(widgetType === "click_to_call" || widgetType === "click_to_text") && (
              <FormGroup label="Subtitle" hint="Optional — shown below heading">
                <input
                  className="input w-full"
                  value={panelSubtitle}
                  onChange={(e) => setPanelSubtitle(e.target.value)}
                  placeholder="We're available Mon–Fri, 9am–5pm"
                />
              </FormGroup>
            )}

            {widgetType === "topic_chat" && (
              <>
                <FormGroup label="Greeting Message">
                  <input
                    className="input w-full"
                    value={greeting}
                    onChange={(e) => setGreeting(e.target.value)}
                    placeholder="Have questions about our products? Just ask!"
                  />
                </FormGroup>
                <FormGroup label="Input Placeholder">
                  <input
                    className="input w-full"
                    value={placeholder}
                    onChange={(e) => setPlaceholder(e.target.value)}
                    placeholder="Type your question here..."
                  />
                </FormGroup>
                <FormGroup label="Route to Team">
                  <select
                    className="input w-full"
                    value={teamId}
                    onChange={(e) => setTeamId(Number(e.target.value))}
                  >
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </FormGroup>
              </>
            )}

            {widgetType === "knowledge_base" && (
              <>
                <div className="rounded-lg bg-primary-bg border border-primary-bg p-3 text-sm text-primary-active">
                  <i className="fa-solid fa-circle-info mr-2" />
                  This widget uses your Knowledge Base to answer visitor questions instantly with AI. No live agent required — great as a standalone product for small businesses.
                </div>
                <FormGroup label="Greeting Message">
                  <input
                    className="input w-full"
                    value={kbGreeting}
                    onChange={(e) => setKbGreeting(e.target.value)}
                    placeholder="Ask us anything!"
                  />
                </FormGroup>
                <FormGroup label="Input Placeholder">
                  <input
                    className="input w-full"
                    value={kbPlaceholder}
                    onChange={(e) => setKbPlaceholder(e.target.value)}
                    placeholder="Type your question..."
                  />
                </FormGroup>
                <FormGroup label="Show Sources" hint="Display links to relevant Knowledge Base articles in the answer">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={showSources}
                      onChange={(e) => setShowSources(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-600">Include source article links in AI answers</span>
                  </div>
                </FormGroup>
                <FormGroup label="Escalate to Live Chat" hint="Let visitors request a live agent if the AI can't answer">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={escalateToChat}
                      onChange={(e) => setEscalateToChat(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-600">Show "Talk to a person" option</span>
                  </div>
                </FormGroup>
                {escalateToChat && (
                  <FormGroup label="Escalation Team">
                    <select
                      className="input w-full"
                      value={teamId}
                      onChange={(e) => setTeamId(Number(e.target.value))}
                    >
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </FormGroup>
                )}
              </>
            )}

            {widgetType === "product_inquiry" && (
              <>
                <div className="rounded-lg bg-primary-bg border border-primary-bg p-3 text-sm text-primary-active">
                  <i className="fa-solid fa-circle-info mr-2" />
                  The widget reads the product name from the page using the CSS selector below, then uses it to personalize the conversation.
                </div>
                <FormGroup label="Product Name Selector" hint="CSS selector that points to the product title on the page">
                  <input
                    className="input w-full font-mono"
                    value={productNameSelector}
                    onChange={(e) => setProductNameSelector(e.target.value)}
                    placeholder="h1"
                  />
                </FormGroup>
                <FormGroup label="Greeting" hint="Use {productName} to insert the product name">
                  <input
                    className="input w-full"
                    value={productGreeting}
                    onChange={(e) => setProductGreeting(e.target.value)}
                    placeholder="Have a question about {productName}?"
                  />
                </FormGroup>
                <FormGroup label="Input Placeholder">
                  <input
                    className="input w-full"
                    value={placeholder}
                    onChange={(e) => setPlaceholder(e.target.value)}
                    placeholder="Ask your question..."
                  />
                </FormGroup>
                <FormGroup label="Route to Team">
                  <select
                    className="input w-full"
                    value={teamId}
                    onChange={(e) => setTeamId(Number(e.target.value))}
                  >
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </FormGroup>
              </>
            )}

            <FormGroup label="Button Text" hint="Optional — leave blank for default">
              <input
                className="input w-full"
                value={buttonText}
                onChange={(e) => setButtonText(e.target.value)}
                placeholder={
                  widgetType === "click_to_call" ? "📞 Call Us"
                  : widgetType === "click_to_text" ? "💬 Text Us"
                  : widgetType === "product_inquiry" ? "Ask a question"
                  : "💬 Chat"
                }
              />
            </FormGroup>

            <FormGroup label="Brand Color">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="w-10 h-10 rounded cursor-pointer border border-gray-200"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                />
                <input
                  className="input flex-1"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#0066CC"
                />
              </div>
            </FormGroup>

            <FormGroup label="Position">
              <div className="flex gap-3">
                {(["bottom-right", "bottom-left"] as const).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPosition(p)}
                    className={`flex-1 py-2 rounded border text-sm transition-colors ${
                      position === p
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {p === "bottom-right" ? "Bottom Right" : "Bottom Left"}
                  </button>
                ))}
              </div>
            </FormGroup>

            <div className="flex justify-between gap-2 mt-2 pt-2 border-t border-gray-100">
              <SecondaryBlueButton label="Back" onClick={() => setStep("type")} />
              <div className="flex gap-2">
                <SecondaryBlueButton label="Cancel" onClick={onClose} />
                <PrimaryBlueButton
                  label={saving ? "Saving..." : "Create Widget"}
                  onClick={handleSave}
                  disabled={saving || !name.trim()}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
